/**
 * Players roster — the pure merge of the Owners Hub Stats API's TWO player data sets.
 *
 * 1. **`select`/`players`** is the BASE list: every registered account on the brand,
 *    with identity, registration date, last login and acquisition source.
 * 2. **`aggregations`/`daily_player_activity`** is the ENRICHMENT: it only has rows
 *    for players who actually wagered or deposited, and carries the money/count
 *    figures. Verified live on `micros-spark-test1`: the same brand has **217**
 *    registrations but only **5** activity rows, so using activity as the roster (as
 *    this page did before) hid ~98% of the owner's players.
 *
 * Kept in its own module — with no Redux import — so it stays unit-testable without
 * pulling the whole store graph in; {@link usePlayersRoster} is the thin hook around it.
 */
import type { Player } from '../../mock-data/types';
import { AMOUNT_SCALE } from '../home/stats-mapping';

/** Micro-units → major currency unit (same factor the rest of Stats uses). */
const MONEY = 1 / AMOUNT_SCALE;
/** The API reports "never logged in" as the epoch, not as null. */
const NEVER_LOGGED_IN = '1970-01-01T00:00:00Z';

export type RawRow = Record<string, unknown>;

/** Coerce a possibly-string scaled amount to a number (the API mixes both). */
const num = (value: unknown): number => {
  const n = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : 0;
  return Number.isFinite(n) ? n : 0;
};

/** A non-empty trimmed string, else undefined (the API uses both `null` and `""`). */
const str = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/** The API's string booleans: `"1"` is true, `"0"` / null / absent is false. */
const flag = (value: unknown): boolean => value === '1' || value === 1 || value === true;

/** An ISO timestamp → epoch ms, or undefined when absent / unparseable. */
const timestamp = (value: unknown): number | undefined => {
  const iso = str(value);
  if (iso === undefined) return undefined;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : undefined;
};

/** The `ftd` column → epoch ms. Its format was never captured live (all-null on the sampled brand): Lumio reads it as
 *  an ISO timestamp, the warehouse marker behind it is an epoch, so both are accepted. */
const firstDeposit = (value: unknown): number | undefined => {
  const raw = str(value) ?? (typeof value === 'number' ? String(value) : undefined);
  if (raw === undefined) return undefined;
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (n <= 0) return undefined;
    // An epoch in seconds until it is large enough to be milliseconds.
    return n < 1e12 ? n * 1000 : n;
  }
  // The same data set writes "never" as the epoch (see NEVER_LOGGED_IN), so a first deposit at or before it is none.
  const ms = timestamp(raw);
  return ms !== undefined && ms > 0 ? ms : undefined;
};

/** `first_name` + `last_name` → a display name, when the brand captured either. */
const fullName = (row: RawRow): string | undefined => {
  const name = [str(row.first_name), str(row.last_name)].filter(Boolean).join(' ');
  return name === '' ? undefined : name;
};

/** The activity half of a `Player` — zeroed for a registration with no activity row. */
const NO_ACTIVITY = {
  wagered: 0,
  wins: 0,
  deposits: 0,
  withdrawals: 0,
  ggr: 0,
  bets: 0,
  depositsCount: 0,
  // No activity row: the player has no bonus or revenue figures to report, so zero is the answer, not a gap.
  bonusGgr: 0,
  bonusAwarded: 0,
  bonusTransformed: 0,
  ngr: 0,
  hasActivity: false,
} as const;

/** The sum of whichever of `keys` the row carries, or undefined when it carries none of them. The bonus and real/bonus
 *  split columns are documented for this data set but were never captured live, so a missing column reads as unknown
 *  ("–") rather than as a measured zero. */
const sumOf = (row: RawRow, keys: readonly string[]): number | undefined => {
  const present = keys.filter((key) => key in row);
  return present.length === 0 ? undefined : present.reduce((sum, key) => sum + num(row[key]), 0);
};

/** Scale an optional raw amount to major units. */
const money = (value: number | undefined): number | undefined => (value === undefined ? undefined : value * MONEY);

/** Map one raw `daily_player_activity` row to the money/count half (major units). */
const toActivity = (row: RawRow): Omit<Player, 'id' | 'email'> => {
  const figures = {
    wagered: (num(row.casino_bet_amount) + num(row.sport_bet_amount)) * MONEY,
    wins: (num(row.casino_win_amount) + num(row.sport_win_amount)) * MONEY,
    deposits: num(row.deposit_amount) * MONEY,
    withdrawals: num(row.withdraw_processed_amount) * MONEY,
    ggr: (num(row.casino_ggr) + num(row.sport_ggr)) * MONEY,
    bets: num(row.casino_bets) + num(row.sport_bets),
    depositsCount: num(row.deposits),
  };
  const ggrReal = sumOf(row, ['casino_ggr_real', 'sport_ggr_real']);
  const bonusTransformed = sumOf(row, ['bonus_amount_transformed']);
  return {
    ...figures,
    bonusGgr: money(sumOf(row, ['casino_ggr_bonus', 'sport_ggr_bonus'])),
    bonusAwarded: money(sumOf(row, ['bonus_amount_granted'])),
    bonusTransformed: money(bonusTransformed),
    // The Stats page's Net Revenue: real-money GGR less the bonus money converted to real (useStatsMetrics.ts).
    ngr: ggrReal === undefined || bonusTransformed === undefined ? undefined : (ggrReal - bonusTransformed) * MONEY,
    // Read off the FIGURES, not off the row existing. The two claims about this data
    // set disagree (`ownerHubStats.ts` records a live check where a registered player
    // with no bets or deposits still had a row; the 217-vs-5 count above says only
    // transactors do), and an all-zero row stamped `hasActivity: true` would match
    // neither the "has bet", "has deposited" nor the "no activity yet" filter — the
    // player would vanish from every cohort. Deriving it is correct under either.
    hasActivity:
      figures.bets > 0 ||
      figures.depositsCount > 0 ||
      figures.wagered > 0 ||
      figures.deposits > 0 ||
      figures.wins > 0 ||
      figures.withdrawals > 0,
  };
};

/** Map one raw `players` row to the registration half. */
const toRegistration = (row: RawRow): Omit<Player, 'id' | 'email' | keyof typeof NO_ACTIVITY> => ({
  playerId: str(row.player_id),
  name: fullName(row),
  country: str(row.tax_code),
  registeredAt: timestamp(row.date_created_tz),
  // "Never signed in" is a real answer, not missing data — but the API encodes it as
  // the epoch, which would otherwise sort as "December 1969" and read as a real date.
  lastLoginAt: str(row.last_login_date) === NEVER_LOGGED_IN ? undefined : timestamp(row.last_login_date),
  source: str(row.aft),
  campaign: str(row.campaign),
  affiliate: str(row.affiliate),
  emailVerified: flag(row.email_verified),
  ftdAt: firstDeposit(row.ftd),
});

/** The row's `email`, lowercased — the only key the two data sets share. */
const emailKey = (row: RawRow): string => (typeof row.email === 'string' ? row.email.trim().toLowerCase() : '');

/** A registration's identity: its email key, or `player:<player_id>` for an account with no email (a phone or social
 *  sign-up). Such a player can't be matched to an activity row, which only carries `email`. */
const registrationKey = (row: RawRow): string => {
  const byEmail = emailKey(row);
  if (byEmail !== '') return byEmail;
  const playerId = str(row.player_id);
  return playerId === undefined ? '' : `player:${playerId}`;
};

/**
 * Merge the registration list with the activity list into the page's `Player[]`.
 *
 * The result is a UNION: every registration (enriched where an activity row matches),
 * plus any activity row with no matching registration — which happens when the
 * registration list hits its fetch cap, and where dropping the row would silently
 * lose a real, and by definition active, customer. Players with no activity row get
 * zeroed money fields and `hasActivity: false`, which the Activity filter keys off.
 */
export const mergePlayers = (registrations: readonly RawRow[], activity: readonly RawRow[]): Player[] => {
  const activityByEmail = new Map<string, RawRow>();
  activity.forEach((row) => {
    const key = emailKey(row);
    if (key !== '') activityByEmail.set(key, row);
  });

  const seen = new Set<string>();
  const players: Player[] = [];

  registrations.forEach((row) => {
    const key = registrationKey(row);
    if (key === '' || seen.has(key)) return;
    seen.add(key);
    const activityRow = activityByEmail.get(key);
    players.push({
      id: key,
      email: str(row.email) ?? '',
      ...(activityRow ? toActivity(activityRow) : NO_ACTIVITY),
      ...toRegistration(row),
    });
  });

  activity.forEach((row) => {
    const key = emailKey(row);
    if (key === '' || seen.has(key)) return;
    seen.add(key);
    players.push({ id: key, email: (row.email as string).trim(), ...toActivity(row) });
  });

  return players;
};
